/**
 * Menu-planning API client — repointed to the rebuilt `/api` surface
 * (#1151 PR-5). The archived `/menu-planning/plans*` face is gone; the
 * single `/api` surface exposes menu planning under `/api/menu/*`.
 * Contract mirrored from `backend/openapi.json` +
 * `backend/src/contexts/kitchen/http/menu.ts`:
 *
 *   GET  /api/menu/plans?status=&week_from=&week_to=&page=&size=
 *                                             → { items,total,page,size }
 *   GET  /api/menu/plans/:id                  → { plan, items }
 *   POST /api/menu/plans                      → MenuPlan (draft)
 *   POST /api/menu/plans/:id/items            → MenuItem
 *   POST /api/menu/plans/:id/approve          → MenuPlan
 *   POST /api/menu/plans/:id/publish          → { plan, items[] }
 *   POST /api/menu/plans/:id/unpublish        → { plan, items[] }
 *   POST /api/menu/plans/:id/republish-fanout → { items[] }  (no body)
 *   POST /api/menu/items/:id/amend            → { menuItemId, shopify }
 *
 * Envelope: every 2xx body is `{ data: T }` (ADR-0006 §5); `http`
 * unwraps the outer `data`, so these methods resolve the inner `T`
 * directly. Wire types come from the generated OpenAPI schema so a
 * backend contract change surfaces at typecheck.
 *
 * Dropped vs the archived client: there is NO plan/item soft-delete and
 * NO ingredient-needs aggregation on the rebuilt face, so `softDelete`,
 * `softDeleteItem` and `computeNeeds` were removed rather than pointed at
 * dead paths. The rebuilt error envelope is `{ error: { code, message } }`
 * with no per-branch `extras` sentinels — callers surface `message`.
 */
import { http } from '@lib/api';
import type { ListPage } from './list-envelope';
import type { components, paths } from '@/types/api.generated';

// ─────────────────────────────────────────────────────────────────
// Wire types — sourced from the generated OpenAPI schema. The response
// bodies below are inline (not named `components.schemas` entries), so
// they are addressed through the generated `paths` tree — a route
// reshape surfaces here at typecheck.
// ─────────────────────────────────────────────────────────────────

export type MenuPlan = components['schemas']['MenuPlan'];
export type MenuItem = components['schemas']['MenuItem'];
export type MenuPlanStatus = MenuPlan['status'];
export type MenuItemStatus = MenuItem['status'];

/** Detail read shape after `http` unwraps the `{ data }` envelope. */
export type MenuPlanDetail =
  paths['/api/menu/plans/{id}']['get']['responses'][200]['content']['application/json']['data'];

/** Flattened detail row — `MenuPlan` augmented with its `items`. The
 *  shared `ResourceDetailPage` renders a single row `T`, so the detail
 *  bridge folds `{ plan, items }` into this shape (see `schema.tsx`). */
export type MenuPlanWithItems = MenuPlan & { items: MenuItem[] };

export type PublishResult =
  paths['/api/menu/plans/{id}/publish']['post']['responses'][200]['content']['application/json']['data'];

/** Per-item Shopify fanout outcome returned by publish / unpublish /
 *  republish-fanout. `kind: 'failed'` marks a soft failure — the DB
 *  transition committed but that item's Shopify write threw. */
export type FanoutItem = PublishResult['items'][number];

export type FanoutResult =
  paths['/api/menu/plans/{id}/republish-fanout']['post']['responses'][200]['content']['application/json']['data'];

export type AmendResult =
  paths['/api/menu/items/{id}/amend']['post']['responses'][200]['content']['application/json']['data'];

// ─────────────────────────────────────────────────────────────────
// Closed-set enums — kept as runtime tuples so the resource schema can
// `.map` them into `EnumOption`s and the wire-leak SOT test can assert
// localisation. Values mirror backend `MENU_PLAN_STATUS` /
// `MENU_ITEM_STATUS` (`backend/src/contexts/kitchen/domain/menu-enums.ts`);
// the `satisfies` clauses pin them to the generated union so a backend
// enum change fails at typecheck instead of silently drifting.
// ─────────────────────────────────────────────────────────────────

export const MENU_PLAN_STATUSES = [
  'draft',
  'approved',
  'published',
] as const satisfies readonly MenuPlanStatus[];

export const MENU_ITEM_STATUSES = [
  'planned',
  'amended',
  'requires_review',
] as const satisfies readonly MenuItemStatus[];

/** Amend reason — mirrors backend `MENU_AMEND_REASON`
 *  (`backend/src/platform/events/events.ts`) and the amend route's zod
 *  `reason` enum. */
export const MENU_AMEND_REASONS = ['combo_changed', 'dish_substituted', 'cancelled'] as const;
export type MenuAmendReason = (typeof MENU_AMEND_REASONS)[number];

// ─────────────────────────────────────────────────────────────────
// Request bodies — narrowed to the exact set each route's zod boundary
// accepts (`backend/src/contexts/kitchen/http/menu.ts`).
// ─────────────────────────────────────────────────────────────────

export interface CreateMenuPlanInput {
  week_start: string;
}

export interface UpsertMenuItemInput {
  delivery_date: string;
  combo_id: string;
  /** Optional JSON snapshot; omit to keep the service default `'[]'`. */
  dishes?: string;
}

export interface AmendMenuItemInput {
  /** New dish-list JSON snapshot for the item. */
  dishes: string;
  reason: MenuAmendReason;
}

// ─────────────────────────────────────────────────────────────────
// List params — the bespoke `/api/menu/plans` query (NOT the generic
// list kernel): a bare `status` enum + `week_from`/`week_to` range +
// page/size. `size` (not `limit`) is the page-size key.
// ─────────────────────────────────────────────────────────────────

export interface MenuPlanListParams {
  status?: MenuPlanStatus;
  week_from?: string;
  week_to?: string;
  page?: number;
  size?: number;
}

export const menuPlanAPI = {
  list: (params: MenuPlanListParams = {}): Promise<ListPage<MenuPlan>> =>
    http.get<ListPage<MenuPlan>>('/api/menu/plans', { params }),

  detail: (id: string): Promise<MenuPlanDetail> =>
    http.get<MenuPlanDetail>(`/api/menu/plans/${encodeURIComponent(id)}`),

  createDraft: (body: CreateMenuPlanInput): Promise<MenuPlan> =>
    http.post<MenuPlan>('/api/menu/plans', body),

  upsertItem: (planId: string, body: UpsertMenuItemInput): Promise<MenuItem> =>
    http.post<MenuItem>(`/api/menu/plans/${encodeURIComponent(planId)}/items`, body),

  approve: (id: string): Promise<MenuPlan> =>
    http.post<MenuPlan>(`/api/menu/plans/${encodeURIComponent(id)}/approve`, {}),

  publish: (id: string): Promise<PublishResult> =>
    http.post<PublishResult>(`/api/menu/plans/${encodeURIComponent(id)}/publish`, {}),

  unpublish: (id: string): Promise<PublishResult> =>
    http.post<PublishResult>(`/api/menu/plans/${encodeURIComponent(id)}/unpublish`, {}),

  /** Re-run the Shopify fanout for a published plan (idempotent retry of
   *  items left `failed` by publish/unpublish). No body — the backend
   *  derives the event type from the plan's current state. 409 if the
   *  plan is not published. */
  republishFanout: (id: string): Promise<FanoutResult> =>
    http.post<FanoutResult>(`/api/menu/plans/${encodeURIComponent(id)}/republish-fanout`, {}),

  /** Amend a published menu item — replace its dish snapshot and
   *  re-render the Shopify title. Path takes the ITEM id (not the plan
   *  id). 409 if the item's plan is not published. */
  amendItem: (itemId: string, body: AmendMenuItemInput): Promise<AmendResult> =>
    http.post<AmendResult>(`/api/menu/items/${encodeURIComponent(itemId)}/amend`, body),
};
